/**
 * The muscle chip's whole behaviour — Shopify's glyph swap, which is the one honest way to draw two
 * degrees without forty-two controls.
 *
 * A chip is in one of three states and says which in its own glyph: `+ Klatka Piersiowa` when it is
 * not chosen, `✓ Klatka Piersiowa 1°` when it is the movement's main work, `✓ … 2°` when it helps.
 * Two controls inside one chip: the BODY cycles in and out, the small trailing degree button moves
 * between the two degrees. That split is what makes the third state reachable without a long press
 * and without a second grid.
 *
 * ── One list or the other, never both ───────────────────────────────────────────────────────
 * `primaryMuscleGroups` and `secondaryMuscleGroups` are two arrays on the wire and nothing stops a
 * caller putting a group in both. It would be read as two different claims about the same muscle:
 * the silhouette paints the stronger fill, the table's pills print it twice, and `similar` ranks
 * the movement against itself. Every operation here goes through `place`, which removes the group
 * from both lists before it adds it to one — so the invariant is a property of the module rather
 * than of the caller's discipline.
 *
 * ── Order is the order they were CHOSEN in ──────────────────────────────────────────────────
 * Not the taxonomy's. A trainer picking the chest first means the chest first, and `similar`, the
 * row's pills and the preview panel all read the first entry as the headline muscle. A sort here
 * would quietly re-headline every movement a trainer authored.
 */

import type { MuscleGroup } from '~/entities/exercise-library';

/** The two lists, as the draft and the wire both hold them. */
export interface MuscleSelection {
  readonly primary: readonly MuscleGroup[];
  readonly secondary: readonly MuscleGroup[];
}

/** What one chip is drawing. `null` for a chip nobody has pressed. */
export type MuscleDegree = 'primary' | 'secondary' | null;

/** Which of the three states a chip is in. */
export function muscleDegree(selection: MuscleSelection, group: MuscleGroup): MuscleDegree {
  if (selection.primary.includes(group)) return 'primary';
  if (selection.secondary.includes(group)) return 'secondary';
  return null;
}

/** Remove from both lists, then add to the one named — or to neither, which is the clear. */
function place(
  selection: MuscleSelection,
  group: MuscleGroup,
  degree: MuscleDegree,
): MuscleSelection {
  const primary = selection.primary.filter((entry) => entry !== group);
  const secondary = selection.secondary.filter((entry) => entry !== group);
  if (degree === 'primary') return { primary: [...primary, group], secondary };
  if (degree === 'secondary') return { primary, secondary: [...secondary, group] };
  return { primary, secondary };
}

/**
 * The chip BODY: nothing → primary, and anything → nothing.
 *
 * Pressing a chosen chip clears it whichever degree it was, rather than stepping 1° → 2° → off.
 * A three-press cycle on the body would make „off" reachable only through a state the trainer did
 * not want, and the degree button is already the way between the two degrees.
 */
export function cycleMuscle(selection: MuscleSelection, group: MuscleGroup): MuscleSelection {
  return place(selection, group, muscleDegree(selection, group) === null ? 'primary' : null);
}

/**
 * The trailing degree button: 1° ↔ 2°.
 *
 * On a chip nobody has chosen it answers the selection untouched. The button is not drawn there, so
 * this is the defensive branch rather than a fourth state — and answering by ADDING the group would
 * make a stray dispatch select a muscle the trainer never pressed.
 */
export function toggleMuscleDegree(
  selection: MuscleSelection,
  group: MuscleGroup,
): MuscleSelection {
  const degree = muscleDegree(selection, group);
  if (degree === null) return selection;
  return place(selection, group, degree === 'primary' ? 'secondary' : 'primary');
}
